import React, { useState, useCallback, useMemo } from "react";
import { useAnalyticsData } from "../../hooks/use-analytics-data";
import Modal, { ModalFooter } from "@amzn/meridian/modal";
import Row from "@amzn/meridian/row";
import Button from "@amzn/meridian/button";
import Text from "@amzn/meridian/text";
import PropTypes from "prop-types";
import Tab, { TabGroup } from "@amzn/meridian/tab";
import Legend, { LegendProvider } from "@amzn/meridian/legend";
import Heading from "@amzn/meridian/heading";
import Column from "@amzn/meridian/column";
import {
  VictoryChart,
  VictoryVoronoiContainer,
  VictoryAxis,
  VictoryLine,
  VictoryBar,
  VictoryStack,
  VictoryTooltip,
} from "victory";
import victoryLinePlugin from "@amzn/meridian/legend/plugin/victory-line";
import victoryTooltipPlugin from "@amzn/meridian/legend/plugin/victory-tooltip";
import useVictoryTheme from "@amzn/meridian/use-victory-theme";
import victoryBarPlugin from "@amzn/meridian/legend/plugin/victory-bar";

import Pie from "@amzn/meridian/pie";
import { convertUnix } from "../../utilities/convert-unix";
import useWarehouseData from "../../hooks/use-warehouse-data";
import { getWarehouseAttributes } from "../../utilities/get-warehouse-attributes";
import Tag from "@amzn/meridian/tag";
import { TruckCalendarView } from "./calendar-view";
import Table, { TableRow, TableCell } from "@amzn/meridian/table";
import { getDisplayQuantity } from "../../utilities/parent-child-conversion";
import { convertParentChild } from "../Constants";


const TAB_CONTENT_STYLE = {
  height: "500px",
  width: "130vh",
  overflowY: "auto",
  overflowX: "hidden",
};

const REGION_2_TEMPZONES = ["AMBIENT-3", "AMBIENT-24", "CHILLED-3", "CHILLED-24", "FROZEN-24"];
const DEFAULT_TEMPZONES = ["AMBIENT", "CHILLED", "FROZEN", "PRODUCE", "PROTEIN"];


const AnalyticsModal = (props) => {
  const { warehouseId, open, onClose} = props;


  // Provide default values directly in the destructuring to handle undefined return
  const { time_zone = "UTC", total_door_count = 1, region_id = 1 } = getWarehouseAttributes({
    warehouseId: warehouseId,
    warehouseParams: useWarehouseData() || [],
  }) || {};

  const [currentTab, setCurrentTab] = useState("tab-trucks");
  const theme = useVictoryTheme({ showIndependentGrid: false });

  // Only fetch and process data when modal is open
  const { shipments1day, shipments2day } = useAnalyticsData(
    warehouseId,
    open,
    time_zone
  );

  const timeSeriesData = useMemo(() => {
    if (!shipments2day || !shipments2day) return [];

    const processData = (shipments) => {
      // Filter out pending loads first
      const filteredShipments = shipments

      const getTimeFromShipment = (shipment) => {
        if (shipment.firstAppointmentTime) {
          return convertUnix(shipment.firstAppointmentTime, time_zone);
        } else if (
          shipment.scheduledArrivals &&
          shipment.scheduledArrivals.length > 0
        ) {
          return convertUnix(shipment.scheduledArrivals[0], time_zone);
        }
        return null; // Return null if no valid time found
      };

      // Filter out shipments with no valid time and get hours
      const hours = filteredShipments
        .map((item) => {
          const time = getTimeFromShipment(item);
          return time ? new Date(time).getHours() : null;
        })
        .filter((hour) => hour !== null);

      if (hours.length === 0) return []; // Return empty array if no valid times found

      const earliestHour = Math.min(...hours);

      const hourlyData = filteredShipments.reduce((acc, item) => {
        const time = getTimeFromShipment(item);
        if (!time) return acc; // Skip items with no valid time

        const hour = new Date(time).getHours();
        if (!acc[hour]) {
          acc[hour] = {
            units: 0,
            count: 0,
          };
        }
        acc[hour].units += getDisplayQuantity(item.expectedUnits, item.childQuantity);
        acc[hour].count += 1;
        return acc;
      }, {});

      return Array.from({ length: 24 - earliestHour }, (_, index) => ({
        hour: `${(earliestHour + index).toString().padStart(2, "0")}:00`,
        rawHour: earliestHour + index,
        value: hourlyData[earliestHour + index]?.units || 0,
      }));
    };

    return [
      {
        key: "today",
        label: "Today",
        data: processData(shipments1day),
      },
      {
        key: "tomorrow",
        label: "Tomorrow",
        data: processData(shipments2day),
      },
    ];
  }, [shipments1day, shipments2day, time_zone]);

  const formatLabel = useCallback(({ datum }) => {
    if (datum.value === 0) {
      return "0";
    }
    return `${datum.value.toLocaleString()}`;
  }, []);
  const tempZoneData = useMemo(() => {
    if (!shipments1day || !shipments2day) return [];

    const zoneTotals = shipments1day
      .filter(
        (shipment) =>
          !shipment.loadId?.includes("Pending") &&
          !(
            Array.isArray(shipment.shipmentId) &&
            shipment.shipmentId.some((id) => id?.includes("TSV"))
          )
      )
      .reduce((acc, item) => {
        if (!acc[item.tempZone]) {
          acc[item.tempZone] = 0;
        }
        acc[item.tempZone] += getDisplayQuantity(item.expectedUnits, item.childQuantity);
        return acc;
      }, {});

    // Transform into required format for pie chart
    return Object.entries(zoneTotals).map(([zone, total]) => ({
      key: zone.toLowerCase(),
      label: zone.charAt(0) + zone.slice(1).toLowerCase(),
      value: total,
    }));
  }, [shipments1day, shipments2day]);

  const tempZoneDataTomorrow = useMemo(() => {
    if (!shipments2day) return [];

    // Sum up units by temperature zone for tomorrow
    const zoneTotals = shipments2day
      .filter(
        (shipment) =>
          !shipment.loadId?.includes("Pending") &&
          !(
            Array.isArray(shipment.shipmentId) &&
            shipment.shipmentId.some((id) => id?.includes("TSV"))
          )
      )
      .reduce((acc, item) => {
        if (!acc[item.tempZone]) {
          acc[item.tempZone] = 0;
        }
        acc[item.tempZone] += getDisplayQuantity(item.expectedUnits, item.childQuantity);
        return acc;
      }, {});

    // Transform into required format for pie chart
    return Object.entries(zoneTotals).map(([zone, total]) => ({
      key: zone.toLowerCase(),
      label: zone.charAt(0) + zone.slice(1).toLowerCase(),
      value: total,
    }));
  }, [shipments2day]);

  const loadTypeData = useMemo(() => {
    if (!shipments2day) return [];

    // Helper function to process data for a given set of shipments and get active sources
    const processLoadTypeData = (shipments) => {
      // Track which sources actually have data
      const activeSources = new Set();
      const sourceTotalUnits = {}; // New object to track total units per source

      const hourlyData = Array.from({ length: 24 }, (_, hour) => {
        const hourlyShipments = shipments.filter((item) => {
          const shipmentTime = new Date(
            convertUnix(
              
                item.firstAppointmentTime ||
                item.expectedArrivalTime[0] ||
                (item.scheduledArrivals && item.scheduledArrivals[0]),
              time_zone
            )
          );
          return shipmentTime.getHours() === hour;
        });

        // Calculate totals by source, using the same grouping logic
        const sourceTotals = hourlyShipments.reduce((acc, item) => {
          if (
            Array.isArray(item.shipmentId) &&
            item.shipmentId.some((id) => id?.includes("TSV"))
          ) {
            return acc;
          }
          let source;
          if (
            item.loadType === "Transship" &&
            item.shipmentId &&
            item.shipmentId.length > 0
          ) {
            source = `${item.source || "Unknown"} - ${item.shipmentId[0]}`;
          } else if (item.distributorName) {
            if (item.distributorName.startsWith("WFM")) {
              source = "WFM";
            } else if (item.distributorName.includes("Spartan Nash")) {
              source = "Spartan Nash";
            } else if (item.distributorName.includes("KeHe")) {
              source = "KeHe";
            } else if (item.distributorName.includes("UNFI")) {
              source = "UNFI";
            } else {
              source = item.distributorName;
            }
          } else if (item.loadId && !item.loadId.includes("Pending")) {
            source = "Unknown Source";
          } else {
            return acc; // Skip this item if we can't determine a source
          }

          if (source) {
            if (!acc[source]) {
              acc[source] = 0;
            }
            acc[source] += getDisplayQuantity(item.expectedUnits, item.childQuantity) || 0;

            // Track total units for each source
            if (!sourceTotalUnits[source]) {
              sourceTotalUnits[source] = 0;
            }
            sourceTotalUnits[source] += getDisplayQuantity(item.expectedUnits, item.childQuantity) || 0;

            // If this source has units, mark it as active
            if (acc[source] > 0) {
              activeSources.add(source);
            }
          }

          return acc;
        }, {});

        return {
          hour: `${hour.toString().padStart(2, "0")}:00`,
          ...sourceTotals,
        };
      });

      return {
        data: hourlyData,
        activeSources: Array.from(activeSources),
        sourceTotalUnits,
      };
    };

    // Process data for both days
    const todayProcessed = processLoadTypeData(shipments1day);
    const tomorrowProcessed = processLoadTypeData(shipments2day);

    // Transform into required format for stacked bar
    return {
      today: todayProcessed.activeSources.map((source) => ({
        key: `${source}-today`,
        label: `${source} (${todayProcessed.sourceTotalUnits[
          source
        ].toLocaleString()} units)`,
        data: todayProcessed.data.map((d) => ({
          x: d.hour,
          value: d[source] || 0,
        })),
      })),
      tomorrow: tomorrowProcessed.activeSources.map((source) => ({
        key: `${source}-tomorrow`,
        label: `${source} (${tomorrowProcessed.sourceTotalUnits[
          source
        ].toLocaleString()} units)`,
        data: tomorrowProcessed.data.map((d) => ({
          x: d.hour,
          value: d[source] || 0,
        })),
      })),
    };
  }, [shipments1day, shipments2day, time_zone]);

  const checkPrevious30Minutes = (timeSlots, currentIndex) => {
    if (currentIndex < 1) return false;

    const currentSlot = timeSlots[currentIndex];
    const prevSlot = timeSlots[currentIndex - 1];

    // Convert time strings to minutes for comparison
    const convertToMinutes = (timeString) => {
      const [hours, minutes] = timeString.split(":").map(Number);
      return hours * 60 + minutes;
    };

    const currentTime = convertToMinutes(currentSlot.timeSlot);
    const prevTime = convertToMinutes(prevSlot.timeSlot);

    // Calculate time difference in minutes
    const timeDifference = Math.abs(currentTime - prevTime);

    // Return true only if times are within 30 minutes AND loads exceed door count
    return (
      timeDifference <= 30 &&
      currentSlot.numberOfLoads + prevSlot.numberOfLoads > total_door_count
    );
  };

  return (
    <Modal
      title="Day Planner"
      open={open}
      onClose={onClose}
      scrollContainer="viewport"
      closeLabel="Close"
      aria-describedby="modal-description"
      style={{ width: "1300px", maxWidth: "1300px" }}
    >
      {open && (
        <>
          <TabGroup
        value={currentTab}
        onChange={setCurrentTab}
        aria-labelledby="tablist-heading"
      >
        <Tab value="tab-trucks" aria-controls="tabpanel-trucks" id="tab-trucks">
          Trucks
        </Tab>
        <Tab
          value="tab-calendar"
          aria-controls="tabpanel-calendar"
          id="tab-calendar"
        >
          Calendar View
        </Tab>
        <Tab
          value="tab-ib-breakout"
          aria-controls="tabpanel-ib-breakout"
          id="tab-ib-breakout"
        >
          IB Breakout
        </Tab>

        <Tab
          value="tab-temp-zone"
          aria-controls="tabpanel-temp-zone"
          id="tab-temp-zone"
        >
          Temp Zone Breakout
        </Tab>

        <Tab value="tab_time" aria-controls="tabpanel-time" id="tab_time">
          Time of Day
        </Tab>
      </TabGroup>

      {currentTab === "tab-trucks" && (
        <div style={TAB_CONTENT_STYLE}>
          <br />
          <Table
            headerRows={1}
            showDividers={true}
            rowComponents={[TableRow]}
            stickyHeaderRow={true}
            stickyHeaderColumn={false}
            showStripes={false}
            spacing="default"
          >
            <TableRow>
              <TableCell width="10%" alignmentHorizontal="center">
                <Text>
                  <strong>Day</strong>
                </Text>
              </TableCell>
              <TableCell width="6%" alignmentHorizontal="center">
                <Text>
                  <strong>Time of Day</strong>
                </Text>
              </TableCell>
              <TableCell width="8%" alignmentHorizontal="center">
                <Text>
                  <strong>Loads</strong>
                </Text>
              </TableCell>
              <TableCell width="20%" alignmentHorizontal="center">
                <Text>
                  <strong>Distributor</strong>
                </Text>
              </TableCell>
              <TableCell width="10%" alignmentHorizontal="center">
                <Text>
                  <strong>{`Total Units${convertParentChild === 1 ? ' Child' : ''}`}</strong>
                </Text>
              </TableCell>
              {(region_id === 2 ? REGION_2_TEMPZONES : DEFAULT_TEMPZONES).map((zone) => (
                <TableCell key={zone} width="8%" alignmentHorizontal="center">
                  <Text>
                    <strong>{zone}</strong>
                  </Text>
                </TableCell>
              ))}
            </TableRow>
            {[
              { label: "Today", data: shipments1day || [] },
              { label: "Tomorrow", data: shipments2day || [] },
            ].map(({ label, data }) => {
              const timeSlots = Array.from({ length: 48 }, (_, index) => {
                const hour = Math.floor(index / 2);
                const minute = (index % 2) * 30;
                const timeSlot = `${hour.toString().padStart(2, "0")}:${minute
                  .toString()
                  .padStart(2, "0")}`;

                const shipmentsInSlot = data.filter((shipment) => {
                  const shipmentTime = new Date(
                    convertUnix(
                        shipment.firstAppointmentTime ||
                        shipment.expectedArrivalTime[0] ||
                        (shipment.scheduledArrivals &&
                          shipment.scheduledArrivals[0]),
                      time_zone
                    )
                  );

                  return (
                    shipmentTime.getHours() === hour &&
                    Math.floor(shipmentTime.getMinutes() / 30) === index % 2
                  );
                });

                // Filter out invalid shipments
                const validShipmentsInSlot = shipmentsInSlot.filter((s) => {
                  if (
                    s.loadId === "PO Pending Schedule" ||
                    s.distributorName === "PO Pending Schedule" ||
                    (Array.isArray(s.shipmentId) &&
                      s.shipmentId.some((id) => id?.includes("TSV")))
                  ) {
                    return false;
                  }

                  if (s.loadType === "Transship") {
                    return s.loadId;
                  } else {
                    if (s.loadId === "Not Yet Manifested") {
                      return s.shipmentId && s.shipmentId.length > 0; // Check for shipmentId array
                    }
                    return s.distributorName && s.distributorName !== "Unknown";
                  }
                });

                if (validShipmentsInSlot.length === 0) return null;

                const uniqueDistributors = [
                  ...new Set(
                    validShipmentsInSlot
                      .map((s) => {
                        if (
                          s.loadType === "Transship" &&
                          s.shipmentId &&
                          s.shipmentId.length > 0
                        ) {
                          return `${s.source || "Unknown"} - ${
                            s.shipmentId[0]
                          }`;
                        } else if (s.distributorName) {
                          if (s.distributorName.startsWith("WFM")) {
                            return "WFM";
                          } else if (
                            s.distributorName.includes("Spartan Nash")
                          ) {
                            return "Spartan Nash";
                          } else if (s.distributorName.includes("KeHe")) {
                            return "KeHe";
                          } else if (s.distributorName.includes("UNFI")) {
                            return "UNFI";
                          } else {
                            return s.distributorName;
                          }
                        } else if (s.loadId && !s.loadId.includes("Pending")) {
                          return "Unknown Source";
                        }
                        return null;
                      })
                      .filter(Boolean)
                  ),
                ];

                const tempZoneUnits = validShipmentsInSlot.reduce((acc, s) => {
                  if (s.tempZone) {
                    acc[s.tempZone] =
                      (acc[s.tempZone] || 0) + (getDisplayQuantity(s.expectedUnits, s.childQuantity) || 0);
                  }
                  return acc;
                }, {});

                const totalUnits = Object.values(tempZoneUnits).reduce(
                  (sum, val) => sum + val,
                  0
                );

                return {
                  timeSlot,
                  numberOfLoads: uniqueDistributors.length,
                  distributors: uniqueDistributors.join(", "),
                  totalUnits,
                  tempZoneUnits,
                };
              }).filter(Boolean);

              return timeSlots.map((slot, index) => (
                <TableRow key={`${label}-${slot.timeSlot}`}>
                  {index === 0 && (
                    <TableCell
                      rowSpan={timeSlots.length}
                      alignmentHorizontal="center"
                    >
                      <Text>{label}</Text>
                    </TableCell>
                  )}
                  <TableCell alignmentHorizontal="center">
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "flex-end", // Right align all timestamps
                        gap: "4px",
                        paddingRight: "20px", // Add some padding from the right edge
                      }}
                    >
                      <Text
                        style={{
                          fontWeight: index === 0 ? "bold" : "normal", // Make first timestamp bold
                        }}
                      >
                        {slot.timeSlot}
                      </Text>
                      <div style={{ display: "flex", gap: "2px" }}>
                        {slot.numberOfLoads > total_door_count ? (
                          <Tag type="error" />
                        ) : (
                          checkPrevious30Minutes(timeSlots, index) && (
                            <Tag type="warning" />
                          )
                        )}
                      </div>
                    </div>
                  </TableCell>
                  <TableCell alignmentHorizontal="center">
                    <Text>{slot.numberOfLoads}</Text>
                  </TableCell>
                  <TableCell alignmentHorizontal="left">
                    <Text>
                      {(() => {
                        const distributors = slot.distributors.split(", ");
                        if (distributors.length <= 10) {
                          return slot.distributors;
                        } else {
                          return distributors.slice(0, 10).join(", ") + ", ...";
                        }
                      })()}
                    </Text>
                  </TableCell>
                  <TableCell alignmentHorizontal="center">
                    <Text>
                      <strong>{slot.totalUnits.toLocaleString()}</strong>
                    </Text>
                  </TableCell>
                  {(region_id === 2 ? REGION_2_TEMPZONES : DEFAULT_TEMPZONES).map((zone) => (
                    <TableCell key={zone} alignmentHorizontal="center">
                      <Text>
                        {(slot.tempZoneUnits[zone] || "-").toLocaleString()}
                      </Text>
                    </TableCell>
                  ))}
                </TableRow>
              ));
            })}
          </Table>

          <div
            style={{ marginTop: "20px", fontSize: "12px", fontStyle: "italic" }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                marginBottom: "8px",
              }}
            >
              <Tag type="warning" />
              <span style={{ marginLeft: "8px" }}>
                Number of incoming loads scheduled within 30 minutes exceeds
                available dock doors.
              </span>
            </div>
            <div style={{ display: "flex", alignItems: "center" }}>
              <Tag type="error" />
              <span style={{ marginLeft: "8px" }}>
                Multiple loads are scheduled to arrive simultaneously, exceeding
                total dock door capacity.
              </span>
            </div>
          </div>
        </div>
      )}

      {currentTab === "tab_time" && (
        <div
          style={{
            ...TAB_CONTENT_STYLE,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <br></br>
          <LegendProvider
            data={timeSeriesData}
            plugins={[
              victoryLinePlugin({ theme }),
              victoryTooltipPlugin({ showCrosshair: true }),
            ]}
            aria-label="Line chart showing expected units by hour"
          >
            {({ getLineProps, voronoiContainerProps, Tooltip }) => (
              <Column spacing="none">
                {/* <Heading level={3}>Expected Units by Hour</Heading> */}
                <VictoryChart
                  theme={theme}
                  width={2000}
                  height={500}
                  name="line-chart-units-by-hour"
                  padding={{ top: 20, bottom: 80, left: 60, right: 20 }}
                  containerComponent={
                    <VictoryVoronoiContainer
                      labels={formatLabel}
                      {...voronoiContainerProps}
                    />
                  }
                >
                  <VictoryAxis
                    tickValues={Array.from(
                      { length: 24 },
                      (_, i) => `${i.toString().padStart(2, "0")}:00`
                    )}
                    tickFormat={(t) => t}
                    style={{
                      tickLabels: {
                        angle: -45,
                        textAnchor: "end",
                        fontSize: 18,
                      },
                    }}
                  />
                  <VictoryAxis
                    dependentAxis
                    label={`Expected Units${convertParentChild === 1 ? ' Child' : ''}`}
                    tickFormat={(value) => {
                      if (value >= 1000) {
                        return `${(value / 1000).toFixed(0)}k`;
                      }
                      return value;
                    }}
                    style={{
                      axisLabel: { padding: 40, fontSize: 15 },
                      tickLabels: {
                        fontSize: 18,
                      },
                    }}
                  />
                  {timeSeriesData.map(({ data, key, label }) => (
                    <VictoryLine
                      key={key}
                      data={data.map((d) => ({
                        ...d,
                        x: d.hour,
                        label,
                      }))}
                      x="hour"
                      y="value"
                      labelComponent={
                        <Tooltip position="top" legendKey={key} />
                      }
                      {...getLineProps(key)}
                    />
                  ))}
                </VictoryChart>
                <Legend direction="horizontal" />
              </Column>
            )}
          </LegendProvider>
        </div>
      )}
      {currentTab === "tab-temp-zone" && (
        <div
          style={{
            ...TAB_CONTENT_STYLE,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              width: "100%",
            }}
          >
            {/* Today's Donut */}
            <Column spacing="500" style={{ marginRight: "200px" }}>
              {" "}
              {/* Changed to margin instead of padding */}
              <Heading level={4} alignmentHorizontal="left">
                Today
              </Heading>
              <LegendProvider
                data={tempZoneData}
                aria-labelledby="temp-zone-distribution-today"
              >
                <Row
                  spacing="300"
                  alignmentHorizontal="left"
                  alignmentVertical="top"
                >
                  <Pie width={300} data={tempZoneData} donutRatio={0.8}>
                    <Text type="d100" tag="div">
                      {tempZoneData
                        .reduce((total, slice) => total + slice.value, 0)
                        .toLocaleString()}
                    </Text>
                    <Text type="h100" tag="div" color="secondary">
                      {`Total Units${convertParentChild === 1 ? ' Child' : ''}`}
                    </Text>
                  </Pie>
                  <Column>
                    <Legend
                      style={{ domainPaddingRight: "200px" }}
                      direction="vertical"
                      values={(item) =>
                        item.value >= 1000
                          ? `${(item.value / 1000).toFixed(1)}k`
                          : item.value.toLocaleString()
                      }
                    />
                  </Column>
                </Row>
              </LegendProvider>
            </Column>
          </div>

          <div
            style={{
              display: "flex",
              justifyContent: "left",
              width: "100%",
            }}
          >
            {/* Tomorrow's Donut */}
            <Column spacing="500" style={{ marginLeft: "200px" }}>
              {" "}
              {/* Changed to margin instead of padding */}
              <Heading level={4} alignmentHorizontal="center">
                Tomorrow
              </Heading>
              <LegendProvider
                data={tempZoneDataTomorrow}
                aria-labelledby="temp-zone-distribution-tomorrow"
              >
                <Row
                  spacing="300"
                  alignmentHorizontal="center"
                  alignmentVertical="top"
                >
                  <Pie width={300} data={tempZoneDataTomorrow} donutRatio={0.8}>
                    <Text type="d100" tag="div">
                      {tempZoneDataTomorrow
                        .reduce((total, slice) => total + slice.value, 0)
                        .toLocaleString()}
                    </Text>
                    <Text type="h100" tag="div" color="secondary">
                      {`Total Units${convertParentChild === 1 ? ' Child' : ''}`}
                    </Text>
                  </Pie>
                  <Column>
                    <Legend
                      direction="vertical"
                      values={(item) =>
                        item.value >= 1000
                          ? `${(item.value / 1000).toFixed(1)}k`
                          : item.value.toLocaleString()
                      }
                    />
                  </Column>
                </Row>
              </LegendProvider>
            </Column>
          </div>
        </div>
      )}

      {currentTab === "tab-ib-breakout" && (
        <div
          style={{
            ...TAB_CONTENT_STYLE,
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            width: "100%",
          }}
        >
          <br />
          <div
            style={{
              display: "flex",
              justifyContent: "space-around",
              width: "100%",
              gap: "20px",
            }}
          >
            {["today", "tomorrow"].map((day) => {
              // Filter non-zero data for each day
              const nonZeroData = loadTypeData[day].filter((item) =>
                item.data.some((d) => d.value > 0)
              );

              return (
                <div key={day} style={{ width: "150%" }}>
                  <LegendProvider
                    data={nonZeroData}
                    plugins={[
                      victoryBarPlugin({ theme }),
                      victoryTooltipPlugin(),
                    ]}
                    aria-label={`Stacked bar chart showing total units by source per hour for ${day}`}
                  >
                    {({ getBarProps, voronoiContainerProps, Tooltip }) => (
                      <Column spacing="none" style={{ width: "150%" }}>
                        <Heading level={5}>
                          {day.charAt(0).toUpperCase() + day.slice(1)}
                        </Heading>
                        <VictoryChart
                          theme={theme}
                          width={900}
                          height={450}
                          domainPadding={{ x: 20, y: 0 }}
                          name={`stacked-bar-chart-sources-${day}`}
                          containerComponent={
                            <VictoryVoronoiContainer
                              labels={({ datum }) =>
                                datum.value > 0 ? formatLabel({ datum }) : null
                              }
                            />
                          }
                        >
                          <VictoryAxis
                            tickValues={Array.from(
                              { length: 24 },
                              (_, i) => `${i.toString().padStart(2, "0")}:00`
                            )}
                            tickFormat={(t) => t}
                            style={{
                              tickLabels: {
                                angle: -45,
                                textAnchor: "end",
                                fontSize: 10,
                              },
                            }}
                          />
                          <VictoryAxis
                            dependentAxis
                            tickFormat={(value) => {
                              if (value === 0) return ""; // Don't show 0
                              if (value >= 1000) {
                                return `${(value / 1000).toFixed(0)}k`;
                              }
                              return value;
                            }}
                          />
                          <VictoryStack>
                            {nonZeroData.map(
                              ({ data, key, label }, stackedIndex) => (
                                <VictoryBar
                                  key={key}
                                  data={data}
                                  x="x"
                                  y="value"
                                  barWidth={18}
                                  labels={
                                    ({ datum }) =>
                                      datum.value > 0 ? label : null // Only show label if value > 0
                                  }
                                  labelComponent={
                                    <VictoryTooltip style={{ fontSize: 18 }} />
                                  }
                                  {...getBarProps(key, { stackedIndex })}
                                />
                              )
                            )}
                          </VictoryStack>
                        </VictoryChart>
                        {/* <Legend direction="horizontal" style={{ fontSize: "12px" }} /> */}
                      </Column>
                    )}
                  </LegendProvider>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {currentTab === "tab-calendar" && (
        <div style={TAB_CONTENT_STYLE}>
          <br />
          <TruckCalendarView
            trucks={[
              ...shipments1day
                .filter(
                  (shipment) =>
                    !shipment.loadId?.includes("Pending") &&
                    !(
                      Array.isArray(shipment.shipmentId) &&
                      shipment.shipmentId.some((id) => id?.includes("TSV"))
                    ) &&
                    (shipment.distributorName ||
                      (shipment.loadId && shipment.loadType === "Transship") ||
                      (shipment.loadType === "Transship" &&
                        shipment.shipmentId &&
                        shipment.shipmentId.length > 0))
                )
                .map((s) => ({
                  ...s,
                  day: "Today",
                })),
              ...shipments2day
                .filter(
                  (shipment) =>
                    !shipment.loadId?.includes("Pending") &&
                    !(
                      Array.isArray(shipment.shipmentId) &&
                      shipment.shipmentId.some((id) => id?.includes("TSV"))
                    ) &&
                    (shipment.distributorName ||
                      (shipment.loadId && shipment.loadType === "Transship") ||
                      (shipment.loadType === "Transship" &&
                        shipment.shipmentId &&
                        shipment.shipmentId.length > 0))
                )
                .map((s) => ({
                  ...s,
                  day: "Tomorrow",
                })),
            ]}
            time_zone={time_zone}
          />
        </div>
      )}

        </>
      )}
      <ModalFooter>
        <Row alignmentHorizontal="end" widths="fit">
          <Button type="secondary" size="small" onClick={onClose}>
            Cancel
          </Button>
        </Row>
      </ModalFooter>
    </Modal>
  );
};

AnalyticsModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  warehouseId: PropTypes.string.isRequired,
};

export default AnalyticsModal;
